import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, Sun, Moon, Rocket } from 'lucide-react';
import { useTheme } from './ThemeProvider';

const LINKS = [
  { to: '/', label: 'Home' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/docs', label: 'Docs' },
  { to: '/developers', label: 'API' },
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQ' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-2xl bg-background/75 border-b border-border/50'
          : 'backdrop-blur-xl bg-background/40 border-b border-transparent'
      }`}
    >
      <div className="container-x h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
          <div className="relative">
            <img
              src={(process.env.PUBLIC_URL || '') + '/assets/logo.png'}
              alt="PumpFun Sniper"
              className="w-9 h-9 object-contain drop-shadow-[0_0_10px_rgba(0,255,102,0.45)] group-hover:animate-pulse-glow"
            />
          </div>
          <span className="font-display font-extrabold text-base tracking-tight">
            PumpFun <span className="gradient-text">Sniper</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-3.5 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground hover:text-foreground'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute inset-x-3 -bottom-0.5 h-[2px] bg-[hsl(var(--primary))] rounded-full shadow-[0_0_8px_rgba(0,255,102,0.7)]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label="Toggle theme"
            data-testid="theme-toggle"
            className="p-2.5 rounded-lg border border-border/70 hover:border-[hsl(var(--primary))]/50 hover:text-[hsl(var(--primary))] transition-colors"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <a
            href="terminal/"
            className="hidden xs:inline-flex btn-primary !px-3 sm:!px-5 !py-2.5 !text-[11px] sm:!text-xs"
            data-testid="launch-terminal-btn"
          >
            <Rocket className="w-4 h-4" /> <span className="hidden sm:inline">Launch Bot</span><span className="sm:hidden">Bot</span>
          </a>
          <button
            className="lg:hidden p-2.5 rounded-lg border border-border/70"
            onClick={() => setOpen(v => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border/50 bg-background/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="container-x py-4 flex flex-col gap-1">
            {LINKS.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive
                      ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                      : 'text-muted-foreground hover:text-foreground'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a href="terminal/" className="btn-primary mt-2 justify-center">
              <Rocket className="w-4 h-4" /> Launch Bot
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
